import { z } from 'zod';

import { zodSuccessResponseSchema } from '@vestigas/data';

import { zodContractStatusSchema } from '../contract';

export const zodArticleSchema = z.object({
  description: z.string().nullish(),
  ean: z.string().nullish(),
  id: z.number().int(),
  manufacturerPartyId: z.number().int().nullish(),
  name: z.string().nullish(),
  sellerAssignedId: z.string().nullish(),
  sellerCompanyId: z.guid().nullish(),
});

export const zodDlnItemDetailsSchema = z.object({
  acceptedQuantityFactor: z.string().nullish(),
  actualDelivery: z.string().nullish(),
  actualDeliveryQuantity: z.string().nullish(),
  additionalPartyData: z.record(z.string(), z.unknown()).nullish(),
  batch: z.string().nullish(),
  billedQuantityFactor: z.coerce.number().nullish(),
  buyerOrder: z.string().nullish(),
  comment: z.string().nullish(),
  constructionComponent: z.string().nullish(),
  constructionPlan: z.string().nullish(),
  contentUuid: z.guid(),
  grossWeight: z.string().nullish(),
  grossWeightDate: z.string().nullish(),
  grossWeightId: z.string().nullish(),
  grossWeightUnit: z.string().nullish(),
  id: z.number().int(),
  normalizedQuantity: z.string().nullish(),
  normalizedQuantityUnit: z.string().nullish(),
  normalizedWeight: z.string().nullish(),
  normalizedWeightUnit: z.string().nullish(),
  productionDate: z.string().nullish(),
  quantity: z.string().nullish(),
  quantityUnit: z.string().nullish(),
  remainingDelivery: z.string().nullish(),
  remainingDeliveryQuantity: z.string().nullish(),
  requestedDelivery: z.string().nullish(),
  requestedDeliveryQuantity: z.string().nullish(),
  sellerOrder: z.string().nullish(),
  tareWeight: z.string().nullish(),
  tareWeightDate: z.string().nullish(),
  tareWeightId: z.string().nullish(),
  tareWeightUnit: z.string().nullish(),
  weighingPartyId: z.number().int().nullish(),
  weighingPartyRoleCode: z.string().nullish(),
  weighingPersonId: z.number().int().nullish(),
  weight: z.string().nullish(),
  weightUnit: z.string().nullish(),
});

export const zodDlnItemConcreteSchema = z.object({
  certificate: z.record(z.string(), z.unknown()).nullish(),
  chlorideClass: z.string().nullish(),
  chlorideContentQuantity: z.string().nullish(),
  chlorideContentQuantityUnit: z.string().nullish(),
  comment: z.string().nullish(),
  components: z.array(z.record(z.string(), z.unknown())).nullish(),
  concrete: z.string().nullish(),
  consistency: z.string().nullish(),
  contentUuid: z.guid(),
  exposureClass: z.string().nullish(),
  grossDensityClass: z.string().nullish(),
  handling: z.record(z.string(), z.unknown()).nullish(),
  id: z.number().int(),
  largestGrain: z.string().nullish(),
  lastValidation: z.string().nullish(),
  moistureClass: z.string().nullish(),
  posteriorAdded: z.array(z.record(z.string(), z.unknown())).nullish(),
  processabilityTime: z.string().nullish(),
  returnedMaterialQuantity: z.string().nullish(),
  returnedMaterialQuantityUnit: z.string().nullish(),
  shippingMarks: z.record(z.string(), z.unknown()).nullish(),
  strengthClass: z.string().nullish(),
  strengthDevelopment: z.string().nullish(),
  type: z.string().nullish(),
  usage: z.string().nullish(),
  waterCementRatio: z.string().nullish(),
});

export const zodDlnItemSchema = z.object({
  accountingReferenceId: z.guid().nullish(),
  accountingReferenceName: z.string().nullish(),
  concreteDetails: zodDlnItemConcreteSchema.nullable().optional(),
  concreteDetailsId: z.number().int().nullish(),
  contentUuid: z.guid(),
  id: z.number().int(),
  internalPosNr: z.number().int().nullish(),
  item: zodArticleSchema.nullable().optional(),
  itemDetails: zodDlnItemDetailsSchema.nullable().optional(),
  itemDetailsId: z.number().int().nullish(),
  itemId: z.number().int().nullish(),
  posNr: z.string().nullish(),
  siteId: z.guid().nullish(),
  siteName: z.string().nullish(),
});

export const zodDeliveryNoteInternalSchema = z.object({
  assetId: z.guid(),
  buyerId: z.guid().nullish(),
  buyerPartyId: z.number().int().nullish(),
  carrierId: z.guid().nullish(),
  carrierPartyId: z.number().int().nullish(),
  // The API returns null while the delivery note is not linked to a contract
  // (LinkedContractSummary | null in the OpenAPI baseline) -- the schema
  // previously required a populated object here.
  contract: z
    .object({
      buyerAssignedId: z.string().nullable(),
      contractName: z.string().nullable(),
      id: z.guid(),
      sellerAssignedId: z.string().nullable(),
      status: zodContractStatusSchema,
      validFrom: z.iso.date().nullable(),
      validUntil: z.iso.date().nullable(),
    })
    .nullable(),
  contractId: z.guid().nullish(),
  createdById: z.guid(),
  createdOn: z.string(),
  date: z.string().nullish(),
  dateTime: z.string().nullish(),
  dlnDetailsId: z.number().int().nullish(),
  dlnItems: z.array(zodDlnItemSchema).default([]),
  dlnNr: z.string(),
  docVersion: z.number().int(),
  executionId: z.number().int().nullish(),
  id: z.guid(),
  issuerId: z.guid().nullish(),
  issuerPartyId: z.number().int().nullish(),
  latestVersion: z.boolean(),
  modifiedOn: z.string().optional(),
  processState: z.string(),
  recipientId: z.guid().nullish(),
  recipientPartyId: z.number().int().nullish(),
  recipientSignedUserId: z.guid().nullish(),
  sellerId: z.guid().nullish(),
  sellerPartyId: z.number().int().nullish(),
  shipFromLocationId: z.number().int().nullish(),
  shipToLocationId: z.number().int().nullish(),
  supplierId: z.guid().nullish(),
  supplierPartyId: z.number().int().nullish(),
  technicalIssuerId: z.guid().nullish(),
  technicalIssuerPartyId: z.number().int().nullish(),
  time: z.string().nullish(),
  traderId: z.guid().nullish(),
  traderPartyId: z.number().int().nullish(),
});

export { zodSuccessResponseSchema };
